import { useState } from 'react';
import { useOp } from '../../api.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Kbd, Loading, Modal, age } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { useRouter } from '../../router.tsx';
import type { ApiEndpoint, ApiFinding, ApiProposal, ApiRun } from './types.ts';

const STATUS_LABEL: Record<ApiEndpoint['status'], [string, string]> = {
  healthy: ['Healthy', 'badge-ok'],
  attention: ['Needs a look', 'badge-review'],
  failing: ['Failing', 'badge-warn'],
  disabled: ['No credentials', 'badge-none'],
  never_run: ['Not run yet', 'badge-none'],
};

const KIND_LABEL: Record<string, string> = {
  unknown_field: 'New field',
  missing_field: 'Field missing',
  type_changed: 'Type changed',
  unmapped_enum: 'Unknown value',
  null_rate_spike: 'Field went empty',
  record_count_anomaly: 'Record count',
  records_rejected: 'Records rejected',
  deprecation_notice: 'Deprecation',
  request_failed: 'Request failed',
};

const TABS = [
  { id: 'endpoints', label: 'Endpoints', key: '1' },
  { id: 'findings', label: 'Findings', key: '2' },
  { id: 'proposals', label: 'Proposals', key: '3' },
  { id: 'runs', label: 'Runs', key: '4' },
] as const;
type Tab = (typeof TABS)[number]['id'];

/**
 * API health (super admin): how each network endpoint is read, whether its data still matches the contract, and the
 * mapping fixes the maintenance layer proposes. Nothing changes without a person confirming it here.
 */
export function ApiHealth() {
  const { query, setQuery } = useRouter();
  const tab = (TABS.find((t) => t.id === query.get('tab'))?.id ?? 'endpoints') as Tab;
  const changes = useChanges();
  const toast = useToast();
  const overview = useOp<{ endpoints: ApiEndpoint[]; deals: Record<string, number> }>('admin.apiHealth.overview', {}, changes);
  const [busy, setBusy] = useState<string | null>(null);

  useKeys(Object.fromEntries(TABS.map((t) => [t.key, () => setQuery({ tab: t.id === 'endpoints' ? null : t.id })])));

  if (overview.error?.status === 403) {
    return (
      <section className="page">
        <h1>API health</h1>
        <Empty>Only owners and admins of the platform operator organization can see network API health.</Empty>
      </section>
    );
  }

  const act = async (label: string, name: string, input: unknown, success: (result: never) => string) => {
    setBusy(label);
    await runWrite(toast, name, input, success as (r: unknown) => string);
    setBusy(null);
  };
  const endpoints = overview.data?.endpoints ?? [];
  const attention = endpoints.filter((e) => e.status === 'failing' || e.status === 'attention').length;

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>API health</h1>
          <p className="muted">
            {endpoints.length} endpoints · {attention} need a look · deals: {overview.data?.deals.validated ?? 0} in the catalog, {overview.data?.deals.normalized ?? 0} waiting for a merchant,{' '}
            {overview.data?.deals.discarded ?? 0} discarded. Visible to the platform operator only.
          </p>
        </div>
        <div className="toolbar">
          <button type="button" className="btn" disabled={busy !== null} onClick={() => act('maintenance', 'admin.apiHealth.runMaintenance', {}, (r: { proposed: unknown[] }) => `${r.proposed.length} new proposal(s)`)}>
            {busy === 'maintenance' ? 'Checking…' : 'Propose fixes now'}
          </button>
          <button type="button" className="btn" disabled={busy !== null} onClick={() => act('samples', 'admin.apiHealth.runSync', { job: 'deals', mode: 'fixtures' }, () => 'Deal sync replayed from recorded samples')}>
            {busy === 'samples' ? 'Running…' : 'Replay samples (deals)'}
          </button>
        </div>
      </header>
      <div className="aff-tabs" role="tablist" aria-label="API health views">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setQuery({ tab: t.id === 'endpoints' ? null : t.id })}>
            {t.label} <Kbd>{t.key}</Kbd>
          </button>
        ))}
      </div>
      {overview.error ? <ErrorBox error={overview.error} onRetry={overview.reload} /> : null}
      {tab === 'endpoints' ? overview.data ? <Endpoints endpoints={endpoints} /> : <Loading /> : null}
      {tab === 'findings' ? <Findings /> : null}
      {tab === 'proposals' ? <Proposals /> : null}
      {tab === 'runs' ? <Runs /> : null}
    </section>
  );
}

function Endpoints({ endpoints }: { endpoints: ApiEndpoint[] }) {
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState<ApiEndpoint | null>(null);
  useKeys({ j: () => setCursor((c) => step(c, 1, endpoints.length)), k: () => setCursor((c) => step(c, -1, endpoints.length)), o: () => endpoints[cursor] && setOpen(endpoints[cursor]!), enter: () => endpoints[cursor] && setOpen(endpoints[cursor]!) }, open === null);
  if (endpoints.length === 0) return <Empty>No network accounts configured.</Empty>;
  return (
    <>
      <table className="table compact" aria-label="Network API endpoints">
        <thead>
          <tr>
            <th>Endpoint</th>
            <th>Account</th>
            <th>Version</th>
            <th>Status</th>
            <th>Last run</th>
            <th>Records in / new / failed</th>
            <th>Findings</th>
            <th>Proposals</th>
          </tr>
        </thead>
        <tbody>
          {endpoints.map((e, i) => (
            <tr key={`${e.contractId}|${e.accountKey}`} className={`clickable ${i === cursor ? 'selected' : ''}`} onClick={() => setOpen(e)}>
              <th>
                {e.label}
                <div className="muted small">{e.contractId}</div>
              </th>
              <td>{e.accountKey}</td>
              <td>
                {e.activeVersion}
                {e.confidence !== 'verified' ? <span className="badge badge-none" title="Field names partly assumed from docs; confirm with a real response">{e.confidence === 'unverified' ? 'unverified' : 'partly verified'}</span> : null}
              </td>
              <td>
                <span className={`badge ${STATUS_LABEL[e.status][1]}`}>{STATUS_LABEL[e.status][0]}</span>
                {e.missingCredentials.length ? <div className="muted small">needs {e.missingCredentials.join(', ')}</div> : null}
              </td>
              <td>{e.lastRun ? `${age(e.lastRun.startedAt)}${e.lastRun.mode === 'fixtures' ? ' (samples)' : ''}` : '—'}</td>
              <td>{e.lastRun ? `${e.lastRun.counts.in} / ${e.lastRun.counts.new} / ${e.lastRun.counts.failed}` : '—'}</td>
              <td>{e.openFindings || '—'}</td>
              <td>{e.openProposals || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {open ? <EndpointDrawer endpoint={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function EndpointDrawer({ endpoint, onClose }: { endpoint: ApiEndpoint; onClose: () => void }) {
  const toast = useToast();
  const [confirmVersion, setConfirmVersion] = useState<string | null>(null);
  return (
    <Drawer title={`${endpoint.label} · ${endpoint.accountKey}`} onClose={onClose} wide>
      <dl className="facts">
        <dt>Contract</dt>
        <dd>
          {endpoint.contractId}@{endpoint.activeVersion}
        </dd>
        <dt>Docs</dt>
        <dd>
          <a href={endpoint.docsUrl} target="_blank" rel="noreferrer">
            {endpoint.docsUrl}
          </a>{' '}
          (checked {endpoint.lastChecked})
        </dd>
        <dt>Credentials</dt>
        <dd>{endpoint.missingCredentials.length ? `Not set: ${endpoint.missingCredentials.join(', ')} — live syncs are off for this account.` : 'Set (values are never shown or stored).'}</dd>
        {endpoint.lastRun?.message ? (
          <>
            <dt>Last message</dt>
            <dd>{endpoint.lastRun.message}</dd>
          </>
        ) : null}
      </dl>
      <h3>Versions</h3>
      <p className="muted small">Every fix is a new version; switching back is the rollback. The active version is what this account syncs with.</p>
      <table className="table compact" aria-label="Contract versions">
        <thead>
          <tr>
            <th>Version</th>
            <th>From</th>
            <th>Based on</th>
            <th>Created</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {endpoint.versions.map((v) => (
            <tr key={v.version}>
              <th>{v.version}</th>
              <td>{v.source === 'code' ? 'shipped' : v.source === 'proposal' ? 'approved proposal' : 'manual'}</td>
              <td>{v.basedOnVersion ?? '—'}</td>
              <td>{v.createdBy ? `${v.createdBy}, ${age(v.createdAt!)}` : '—'}</td>
              <td>{v.version === endpoint.activeVersion ? <span className="badge badge-ok">active</span> : <button type="button" className="btn btn-small" onClick={() => setConfirmVersion(v.version)}>Use this version</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {confirmVersion ? (
        <Modal
          title={`Switch ${endpoint.accountKey} to ${confirmVersion}?`}
          onClose={() => setConfirmVersion(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setConfirmVersion(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  await runWrite(toast, 'admin.apiHealth.selectVersion', { contractId: endpoint.contractId, accountKey: endpoint.accountKey, version: confirmVersion, confirm: true }, `Now syncing with ${confirmVersion}`);
                  setConfirmVersion(null);
                  onClose();
                }}
              >
                Switch version
              </button>
            </>
          }
        >
          <p>The next sync reads {endpoint.contractId} with version {confirmVersion}. You can switch back at any time.</p>
        </Modal>
      ) : null}
    </Drawer>
  );
}

function Findings() {
  const changes = useChanges();
  const toast = useToast();
  const [status, setStatus] = useState<'open' | 'all'>('open');
  const list = useOp<{ findings: ApiFinding[] }>('admin.apiHealth.findings', { status }, changes);
  const [open, setOpen] = useState<ApiFinding | null>(null);
  const findings = list.data?.findings ?? [];
  const [cursor, setCursor] = useState(0);
  const setFindingStatus = (f: ApiFinding, next: 'acknowledged' | 'ignored' | 'open') => runWrite(toast, 'admin.apiHealth.setFindingStatus', { id: f.id, status: next }, `Finding ${next}`);
  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, findings.length)),
      k: () => setCursor((c) => step(c, -1, findings.length)),
      o: () => findings[cursor] && setOpen(findings[cursor]!),
      enter: () => findings[cursor] && setOpen(findings[cursor]!),
      a: () => findings[cursor] && void setFindingStatus(findings[cursor]!, 'acknowledged'),
      i: () => findings[cursor] && void setFindingStatus(findings[cursor]!, 'ignored'),
    },
    open === null,
  );
  return (
    <>
      <div className="toolbar">
        <label>
          Show{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value as 'open' | 'all')}>
            <option value="open">Open</option>
            <option value="all">All</option>
          </select>
        </label>
        <span className="muted small">
          <Kbd>a</Kbd> acknowledge · <Kbd>i</Kbd> ignore · <Kbd>o</Kbd> details. Resolved means a new version fixed it or it stopped happening.
        </span>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && findings.length === 0 ? <Empty>No findings. Every endpoint matches its contract.</Empty> : null}
      {findings.length ? (
        <table className="table compact" aria-label="Drift findings">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Endpoint</th>
              <th>What happened</th>
              <th>Seen</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f, i) => (
              <tr key={f.id} className={`clickable ${i === cursor ? 'selected' : ''}`} onClick={() => setOpen(f)}>
                <th>{KIND_LABEL[f.kind] ?? f.kind}</th>
                <td>
                  {f.contract}
                  <div className="muted small">{f.accountKey}</div>
                </td>
                <td>{f.message}</td>
                <td>
                  {f.count}× · {age(f.lastSeenAt)}
                </td>
                <td>{f.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? (
        <Drawer title={`${KIND_LABEL[open.kind] ?? open.kind}: ${open.fieldPath || open.contract}`} onClose={() => setOpen(null)} wide>
          <p>{open.message}</p>
          <h3>Detail</h3>
          <pre className="code-block">{JSON.stringify(open.detail, null, 2)}</pre>
          <h3>Sample records</h3>
          <pre className="code-block">{JSON.stringify(open.sample, null, 2)}</pre>
          <div className="toolbar">
            <button type="button" className="btn" onClick={() => void setFindingStatus(open, 'acknowledged').then(() => setOpen(null))}>
              Acknowledge
            </button>
            <button type="button" className="btn" onClick={() => void setFindingStatus(open, 'ignored').then(() => setOpen(null))}>
              Ignore
            </button>
          </div>
        </Drawer>
      ) : null}
    </>
  );
}

function Proposals() {
  const changes = useChanges();
  const toast = useToast();
  const [status, setStatus] = useState<'proposed' | 'all'>('proposed');
  const list = useOp<{ proposals: ApiProposal[] }>('admin.apiHealth.proposals', { status }, changes);
  const [open, setOpen] = useState<ApiProposal | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const proposals = list.data?.proposals ?? [];
  const [cursor, setCursor] = useState(0);
  useKeys({ j: () => setCursor((c) => step(c, 1, proposals.length)), k: () => setCursor((c) => step(c, -1, proposals.length)), o: () => proposals[cursor] && setOpen(proposals[cursor]!), enter: () => proposals[cursor] && setOpen(proposals[cursor]!) }, open === null);

  return (
    <>
      <div className="toolbar">
        <label>
          Show{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value as 'proposed' | 'all')}>
            <option value="proposed">Waiting for a decision</option>
            <option value="all">All</option>
          </select>
        </label>
        <span className="muted small">Proposals come from the AI model when one is configured, otherwise from rules; every one is replayed against recorded records before you see it.</span>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && proposals.length === 0 ? <Empty>No proposals waiting.</Empty> : null}
      {proposals.length ? (
        <table className="table compact" aria-label="Mapping proposals">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Change</th>
              <th>Replay</th>
              <th>Confidence</th>
              <th>By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map((p, i) => (
              <tr key={p.ref} className={`clickable ${i === cursor ? 'selected' : ''}`} onClick={() => setOpen(p)}>
                <th>
                  {p.contractId}
                  <div className="muted small">
                    {p.baseVersion} → {p.proposedVersion} · {p.accountKey}
                  </div>
                </th>
                <td>{p.explanation.split('\n')[0]}</td>
                <td>{p.replayPassed ? <span className="badge badge-ok">passed</span> : <span className="badge badge-warn">failed</span>}</td>
                <td>{Math.round(p.confidence * 100)}%</td>
                <td>{p.generator}</td>
                <td>{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? (
        <Drawer
          title={`${open.contractId}: ${open.baseVersion} → ${open.proposedVersion}`}
          onClose={() => {
            setOpen(null);
            setConfirming(false);
          }}
          wide
          footer={
            open.status === 'proposed' ? (
              <div className="toolbar">
                <input aria-label="Reason for rejecting" placeholder="Reason (to reject)" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
                <button
                  type="button"
                  className="btn btn-danger-ghost"
                  disabled={rejectNote.trim().length < 3}
                  onClick={async () => {
                    await runWrite(toast, 'admin.apiHealth.rejectProposal', { ref: open.ref, note: rejectNote.trim() }, 'Proposal rejected');
                    setRejectNote('');
                    setOpen(null);
                  }}
                >
                  Reject
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)}>
                  Apply as {open.proposedVersion}…
                </button>
              </div>
            ) : undefined
          }
        >
          <p style={{ whiteSpace: 'pre-wrap' }}>{open.explanation}</p>
          <h3>Replay</h3>
          <ReplaySummary replay={open.replay} />
          <h3>Mapping diff</h3>
          <table className="table compact" aria-label="Mapping diff">
            <thead>
              <tr>
                <th>Path</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {open.diff.map((d) => (
                <tr key={d.path}>
                  <th>{d.path}</th>
                  <td>
                    <code>{d.before === undefined ? '—' : JSON.stringify(d.before)}</code>
                  </td>
                  <td>
                    <code>{d.after === undefined ? '—' : JSON.stringify(d.after)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {confirming ? (
            <Modal
              title={`Apply ${open.proposedVersion}?`}
              onClose={() => setConfirming(false)}
              actions={
                <>
                  <button type="button" className="btn" onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      const done = await runWrite<{ version: string; previousVersion: string | null }>(toast, 'admin.apiHealth.applyProposal', { ref: open.ref, confirm: true }, (r) => `Applied: ${open.accountKey} now syncs with ${r.version} (was ${r.previousVersion ?? '—'})`);
                      if (done) {
                        setConfirming(false);
                        setOpen(null);
                      }
                    }}
                  >
                    Apply
                  </button>
                </>
              }
            >
              <p>
                This stores {open.proposedVersion} as a new contract version and selects it for {open.accountKey}. {open.baseVersion} stays available: switch back on the endpoint at any time.
              </p>
              {!open.replayPassed ? <p className="error">The replay did not pass. Apply only if you checked the samples yourself.</p> : null}
            </Modal>
          ) : null}
        </Drawer>
      ) : null}
    </>
  );
}

function ReplaySummary({ replay }: { replay: ApiProposal['replay'] }) {
  if (!replay || typeof replay !== 'object' || !('verdict' in replay)) return <p className="muted">No replay data.</p>;
  const r = replay as { verdict: string; records: number; before: { accepted: number; findings: number }; after: { accepted: number; findings: number }; sampleDiffs: { record: number; field: string; before: unknown; after: unknown }[] };
  return (
    <>
      <p>
        <strong>{r.verdict}</strong>
      </p>
      <p className="muted small">
        {r.records} records · mapped {r.before.accepted} → {r.after.accepted} · findings {r.before.findings} → {r.after.findings}
      </p>
      {r.sampleDiffs.length ? (
        <table className="table compact" aria-label="Sample differences">
          <thead>
            <tr>
              <th>Record</th>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            {r.sampleDiffs.map((d, i) => (
              <tr key={i}>
                <td>{d.record + 1}</td>
                <th>{d.field}</th>
                <td>
                  <code>{JSON.stringify(d.before)}</code>
                </td>
                <td>
                  <code>{JSON.stringify(d.after)}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

function Runs() {
  const changes = useChanges();
  const list = useOp<{ runs: ApiRun[] }>('admin.apiHealth.runs', { limit: 100 }, changes);
  if (list.error) return <ErrorBox error={list.error} onRetry={list.reload} />;
  if (!list.data) return <Loading />;
  if (list.data.runs.length === 0) return <Empty>No runs yet. Workers run them on schedule once credentials are set; "Replay samples" runs one now.</Empty>;
  return (
    <table className="table compact" aria-label="Sync runs">
      <thead>
        <tr>
          <th>When</th>
          <th>Job</th>
          <th>Endpoint</th>
          <th>Mode</th>
          <th>Status</th>
          <th>In / new / updated / skipped / failed</th>
          <th>Findings</th>
          <th>Message</th>
        </tr>
      </thead>
      <tbody>
        {list.data.runs.map((r) => (
          <tr key={r.id}>
            <td>{age(r.startedAt)}</td>
            <td>{r.job}</td>
            <th>
              {r.contract}
              <div className="muted small">{r.accountKey}</div>
            </th>
            <td>{r.mode === 'fixtures' ? 'samples' : 'live'}</td>
            <td>{r.status}</td>
            <td>
              {r.counts.in} / {r.counts.new} / {r.counts.updated} / {r.counts.skipped} / {r.counts.failed}
            </td>
            <td>{r.findings || '—'}</td>
            <td className="small">{r.message ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


